import { SyntheticEvent, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import { Button, FormControl, Typography } from "@mui/material";
import dayjs from "dayjs";

import {
  AutocompleteFieldStyle,
  CreateAuthContainer,
  EligibilityFormStyle,
  EligibilityMessageStyle,
  InputHeader,
  SearchButton,
  TextFieldStyle,
  TextSubHeader,
} from "../../CreatePriorAuth/style/style";
import { AuthTextLabel, InformationText } from "../../../app/common/TextLabel";
import { FormInputText } from "../../../components/AuthFormComponents/FormInputText";
import DatePicker from "../../../components/AuthFormComponents/DatePicker";
import { rules } from "../../../utils/validation/Validation";
import { FormSelectAutocomplete } from "../../../components/AuthFormComponents/FormSelect";
import { ListOptions } from "../../../models/Master";
import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import { TextConstants } from "../../../constants/Patient";
import { CheckEligibility, EligibilityCheck } from "../../../models/Patient";
import { DateFormats } from "../../../constants/Date";
import { getCurrnetPageFullPath, getHealthPlanValueType, getUaserPageViewPayload, isMemberEligible } from "../../../utils";
import { Numbers } from "../../../constants/Referral";
import { ApplicationTitles } from "../../../constants/HomePage";
import { redirectUri } from "../../../constants/Config";
import { getValidMessageType } from "../../../constants/User";
import { UserPageTrack } from "../../../models/User";
import PatientHealthPlanVerbiage from "../../PriorAuthWorkFlow/components/PatientHealthPlanVerbiage";
import { getType } from "../../../constants/eligibility";

export interface PropsFromState {
  healthPlanState: ListOptions[];
  checkEligibilityState: CheckEligibility;
}
export interface PropsFromDispatch {
  getMasterHealthPlan: () => void;
  checkEligibility: (payload: EligibilityCheck) => void;
  resetCheckEligibility: () => void
  postUserPageTrack: (payload: UserPageTrack) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const EligibilityChecker: React.FC<AllProps> = ({
  healthPlanState,
  getMasterHealthPlan,
  checkEligibility,
  checkEligibilityState,
  resetCheckEligibility,
  postUserPageTrack,
}) => {
  const methods = useForm();
  const { handleSubmit, control, getValues } = methods;
  const [warningType, setWarningType] = useState<number>(0);

  useEffect(() => {
    getMasterHealthPlan();
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.CHECK_ELIGIBILITY),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
    return () => {
      resetCheckEligibility();
    };
  }, []);

  const onSubmit: SubmitHandler<EligibilityCheck | any> = (
    data: EligibilityCheck
  ) => {
    const payload = {
      lastName: data.lastName,
      dob: dayjs(data?.dob).format(DateFormats.YYYY_MM_DD),
      policyId: data?.policyId,
      payerId: getHealthPlanValueType(getValues().payerName, Numbers.ZERO),
      payerName: getHealthPlanValueType(getValues().payerName, Numbers.ONE),
    };

    checkEligibility(payload);
  };

  const checkEligibilityStatus = () => {
    const isEligible = isMemberEligible(checkEligibilityState);

    const statusText = isEligible
      ? TextConstants.ELIGIBLE
      : TextConstants.NOT_ELIGIBLE;
    return (
      <Typography
        variant="h3"
        color={colors.black[1]}
        fontWeight={fontWeight[6]}
      >
        {statusText}
      </Typography>
    );
  };

  const onHealthPlanSelection = (
    _e: SyntheticEvent<Element, Event>,
    value: ListOptions | null
  ) => {
    if (!value) {
      setWarningType(0);
      return;
    }
    setWarningType(getType(value));
  };

  return (
    <Grid container sx={CreateAuthContainer}>
      <Grid size={{ xs: 12 }} sx={EligibilityMessageStyle}>
        {InformationText(
          TextConstants.CHECK_ELIGIBILITY_TAB_MESSAGE,
          fontWeight[4],
          {},
          colors.black[1]
        )}
      </Grid>
      <Grid size={{ xs: 12 }} sx={EligibilityFormStyle}>
        <Grid pr={2} sx={InputHeader}>
          {AuthTextLabel("Last Name:", true)}
          <FormControl variant="standard">
            <FormInputText
              name="lastName"
              control={control}
              helper={rules.lastNameOnlyAlphabets}
              style={TextFieldStyle(true)}
              textLength={64}
            />
          </FormControl>
        </Grid>
        <Grid pr={2} sx={InputHeader}>
          {AuthTextLabel("Date Of Birth:", true)}
          <FormControl>
            <DatePicker
              name="dob"
              control={control}
              helper={rules.dateOfBirthRequired}
              allowFutureDate={false}
              readOnly={false}
              InputStyle={TextFieldStyle(true)}
            />
          </FormControl>
        </Grid>
        <Grid pr={2} sx={InputHeader}>
          {AuthTextLabel("Subscriber Id:", true)}
          <FormControl variant="standard">
            <FormInputText
              name="policyId"
              control={control}
              helper={rules.subscriberId}
              style={TextFieldStyle(true)}
              textLength={15}
            />
          </FormControl>
        </Grid>
        <Grid pr={2} sx={InputHeader}>
          {AuthTextLabel("Health Plan:", true)}
          <FormSelectAutocomplete
            name="payerName"
            control={control}
            style={AutocompleteFieldStyle(true)}
            list={healthPlanState}
            helper={rules.checkEligibilityHealthPlan}
            onChangeHandler={onHealthPlanSelection}
          />
        </Grid>
        <Grid pr={1} pt={2.5} sx={InputHeader}>
          <Button
            variant="contained"
            style={SearchButton}
            onClick={() => handleSubmit(onSubmit)()}
          >
            SEARCH
          </Button>
        </Grid>
      </Grid>
      {[Numbers.ONE, Numbers.THREE, Numbers.Four, Numbers.Five].includes(warningType) && (
        <PatientHealthPlanVerbiage warningType={warningType} />
      )}
      {checkEligibilityState?.additionalNotes?.eligibilityState !== Numbers.ZERO && (
        <Grid size={{ xs: 12 }} sx={TextSubHeader} pt={1}>
          {checkEligibilityStatus()}
        </Grid>
      )}
    </Grid>
  );
};

export default EligibilityChecker;
